import {
  CURRENT_VOTER_REGISTRATION_VALUES,
  REGISTERED_TO_VOTE_VALUES,
} from '@brushfire/types'
import type { DemographicPanelId } from './audienceTypes'

export interface CheckboxOption {
  value: string
  label: string
}

export interface DemographicPanelDef {
  id: DemographicPanelId
  title: string
  testId: string
  kind: 'ageRange' | 'checkbox' | 'location' | 'devicePills' | 'senateDistrict'
  options?: CheckboxOption[]
}

export const GENDER_OPTIONS: CheckboxOption[] = [
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'non_binary', label: 'Non-binary' },
]

export const EDUCATION_OPTIONS: CheckboxOption[] = [
  { value: 'high_school', label: 'High School' },
  { value: 'some_college', label: 'Some College' },
  { value: 'bachelors', label: "Bachelor's Degree" },
  { value: 'masters', label: "Master's Degree" },
  { value: 'doctorate', label: 'Doctorate' },
  { value: 'trade', label: 'Trade or Vocational' },
]

export const EMPLOYMENT_OPTIONS: CheckboxOption[] = [
  { value: 'full_time', label: 'Employed Full-time' },
  { value: 'part_time', label: 'Employed Part-time' },
  { value: 'self_employed', label: 'Self-employed' },
  { value: 'student', label: 'Student' },
  { value: 'unemployed', label: 'Unemployed' },
  { value: 'retired', label: 'Retired' },
]

export const MARITAL_OPTIONS: CheckboxOption[] = [
  { value: 'single', label: 'Single' },
  { value: 'married', label: 'Married' },
  { value: 'divorced', label: 'Divorced' },
  { value: 'widowed', label: 'Widowed' },
  { value: 'domestic_partnership', label: 'Domestic Partnership' },
]

export const HOUSEHOLD_OPTIONS: CheckboxOption[] = [
  { value: '1', label: '1 — Just Me' },
  { value: '2', label: '2 People' },
  { value: '3', label: '3 People' },
  { value: '4', label: '4 People' },
  { value: '5', label: '5 People' },
  { value: '6', label: '6 People' },
  { value: '7_plus', label: '7+ People' },
]

export const CHILDREN_OPTIONS: CheckboxOption[] = [
  { value: 'none', label: 'None' },
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
  { value: '5_plus', label: '5+' },
]

export const HOUSING_OPTIONS: CheckboxOption[] = [
  { value: 'rent', label: 'Rent' },
  { value: 'own', label: 'Own' },
  { value: 'other', label: 'Other' },
]

export const INDUSTRY_OPTIONS: CheckboxOption[] = [
  { value: 'technology', label: 'Technology/IT' },
  { value: 'healthcare', label: 'Healthcare/Medical' },
  { value: 'finance', label: 'Finance/Banking' },
  { value: 'education', label: 'Education' },
  { value: 'retail', label: 'Retail/E-commerce' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'construction', label: 'Construction' },
  { value: 'transportation', label: 'Transportation/Logistics' },
  { value: 'energy', label: 'Energy/Utilities' },
  { value: 'telecom', label: 'Telecommunications' },
  { value: 'government', label: 'Government/Public Sector' },
  { value: 'legal', label: 'Legal Services' },
  { value: 'media', label: 'Media/Entertainment' },
  { value: 'hospitality', label: 'Hospitality/Tourism' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'agriculture', label: 'Agriculture/Farming' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'consulting', label: 'Consulting/Professional Services' },
  { value: 'marketing', label: 'Marketing/Advertising' },
  { value: 'nonprofit', label: 'Nonprofit/NGO' },
  { value: 'science', label: 'Science/Research' },
  { value: 'automotive', label: 'Automotive' },
  { value: 'aerospace', label: 'Aerospace/Aviation' },
  { value: 'pharma', label: 'Pharmaceuticals/Biotechnology' },
  { value: 'food_beverage', label: 'Food & Beverage' },
  { value: 'arts', label: 'Arts/Design' },
  { value: 'hr', label: 'Human Resources' },
  { value: 'customer_service', label: 'Customer Service' },
  { value: 'sales', label: 'Sales' },
  { value: 'military', label: 'Military/Defense' },
  { value: 'self_employed_ind', label: 'Self-employed' },
  { value: 'student_ind', label: 'Student' },
  { value: 'unemployed_ind', label: 'Unemployed' },
  { value: 'other_ind', label: 'Other' },
]

/** Re-export shared catalog — keeps existing `labelForOption(US_STATES, …)` call sites stable. */
export { CITIES_BY_STATE, US_STATE_OPTIONS as US_STATES } from '@brushfire/types'

/** Checkbox option lists keyed by panel id — used for header summaries and filter panels. */
export const REGISTERED_TO_VOTE_OPTIONS: CheckboxOption[] = REGISTERED_TO_VOTE_VALUES.map(
  (value) => ({ value, label: value }),
)

export const CURRENT_VOTER_REGISTRATION_OPTIONS: CheckboxOption[] =
  CURRENT_VOTER_REGISTRATION_VALUES.map((value) => ({ value, label: value }))

export const PANEL_CHECKBOX_OPTIONS = {
  gender: GENDER_OPTIONS,
  education: EDUCATION_OPTIONS,
  employment: EMPLOYMENT_OPTIONS,
  maritalStatus: MARITAL_OPTIONS,
  householdSize: HOUSEHOLD_OPTIONS,
  numberOfChildren: CHILDREN_OPTIONS,
  industry: INDUSTRY_OPTIONS,
  housingStatus: HOUSING_OPTIONS,
  registeredToVote: REGISTERED_TO_VOTE_OPTIONS,
  currentVoterRegistration: CURRENT_VOTER_REGISTRATION_OPTIONS,
} as const satisfies Partial<Record<DemographicPanelId, CheckboxOption[]>>

export const SENATE_DISTRICT_PANEL_OPTIONS: CheckboxOption[] = [
  { value: 'SD 6', label: 'SD 6' },
  { value: 'SD 16', label: 'SD 16' },
  { value: 'SD 24', label: 'SD 24' },
  { value: 'SD 36', label: 'SD 36' },
  { value: 'SD 40', label: 'SD 40' },
]

export function senateDistrictPanelTitle(required: boolean): string {
  return required ? 'Senate District *' : 'Senate District'
}

export const DEMOGRAPHIC_PANELS: DemographicPanelDef[] = [
  { id: 'ageRange', title: 'Age Range', testId: 'publish-filter-age-range', kind: 'ageRange' },
  {
    id: 'senateDistrict',
    title: 'Senate District',
    testId: 'publish-filter-senate-district',
    kind: 'senateDistrict',
  },
  { id: 'location', title: 'Location', testId: 'publish-filter-location', kind: 'location' },
  { id: 'gender', title: 'Gender', testId: 'publish-filter-gender', kind: 'checkbox', options: GENDER_OPTIONS },
  {
    id: 'employment',
    title: 'Employment Status',
    testId: 'publish-filter-employment',
    kind: 'checkbox',
    options: EMPLOYMENT_OPTIONS,
  },
  {
    id: 'education',
    title: 'Education Level',
    testId: 'publish-filter-education',
    kind: 'checkbox',
    options: EDUCATION_OPTIONS,
  },
  {
    id: 'maritalStatus',
    title: 'Marital Status',
    testId: 'publish-filter-marital-status',
    kind: 'checkbox',
    options: MARITAL_OPTIONS,
  },
  { id: 'deviceType', title: 'Device Type', testId: 'publish-filter-device-type', kind: 'devicePills' },
  {
    id: 'numberOfChildren',
    title: 'No. of Children',
    testId: 'publish-filter-number-of-children',
    kind: 'checkbox',
    options: CHILDREN_OPTIONS,
  },
  {
    id: 'householdSize',
    title: 'Household Size',
    testId: 'publish-filter-household-size',
    kind: 'checkbox',
    options: HOUSEHOLD_OPTIONS,
  },
  {
    id: 'housingStatus',
    title: 'Housing Status',
    testId: 'publish-filter-housing-status',
    kind: 'checkbox',
    options: HOUSING_OPTIONS,
  },
  {
    id: 'industry',
    title: 'Industry/Field of Work',
    testId: 'publish-filter-industry',
    kind: 'checkbox',
    options: INDUSTRY_OPTIONS,
  },
  {
    id: 'registeredToVote',
    title: 'Currently Registered to Vote',
    testId: 'publish-filter-registered-to-vote',
    kind: 'checkbox',
    options: REGISTERED_TO_VOTE_OPTIONS,
  },
  {
    id: 'currentVoterRegistration',
    title: 'Current Voter Registration',
    testId: 'publish-filter-current-voter-registration',
    kind: 'checkbox',
    options: CURRENT_VOTER_REGISTRATION_OPTIONS,
  },
]
